// The console's side of the customer app's two modes (ADR 0035, spec 2.4 and 2.6). The rules live
// in @vd/ui (lib/modes.ts); this file adds what only the console needs: App mode is another app on
// the same origin, so reaching it is a full page load.

import { landingMode, modeOfPath, modesOf, readLastMode, type SwitchMode, switchModes } from "@vd/ui";
import type { Me } from "../api/types";

/**
 * `sessionStorage`: a toast for App mode to show once it loads ("That page is for admins."). The
 * page that refused the address cannot show it, since the browser is leaving.
 */
export const MODE_NOTICE_KEY = "kite.mode.notice";

type Who = Pick<Me, "roles" | "modes" | "web_app" | "platform_url">;

/**
 * The segments of the switch in Admin mode (DESIGN.md "Mode switch"): App once this server
 * serves App mode on the web (`Me.web_app`; until then App has no address to go to), Admin, and
 * Platform for Kite staff (`Me.platform_url`).
 */
export function consoleSwitchModes(me: Who): SwitchMode[] {
  const m = modesOf(me);
  return switchModes({ app: m.app && me.web_app === true, admin: m.admin }, me.platform_url);
}

/** Whether the member sees the switch: two segments or more. */
export function showsModeSwitch(me: Who): boolean {
  return consoleSwitchModes(me).length > 1;
}

/**
 * Where `/` sends a member (CA8). With Admin: the mode the landing rule picks. Managers without
 * Admin keep their pages in the console until App mode has them (CA25). Everyone else: App.
 * `setupUnfinished` is false until Admin Home's checklist exists (phase 3). While this server
 * does not serve App mode (`Me.web_app` false), nobody is sent to `/app`: the console lands as it
 * always has.
 */
export function landingPath(me: Who, lastMode = readLastMode()): string {
  const modes = modesOf(me);
  if (me.web_app !== true) return me.roles.manager && !modes.admin ? "/admin/live-now" : "/admin";
  if (modes.admin) {
    const mode = landingMode({ roles: me.roles, modes, lastMode, setupUnfinished: false });
    return mode === "admin" ? "/admin" : "/app";
  }
  if (me.roles.manager) return "/admin/live-now";
  return "/app";
}

/** True when the address belongs to App mode, which this app cannot route to. */
export function isAppPath(href: string): boolean {
  return modeOfPath(href.split(/[?#]/)[0] ?? href) === "app";
}

/** How the console loads another app's page. Tests replace `load`. */
export const pageLoad = {
  load: (href: string) => window.location.replace(href),
};

/** Loads another app's address, leaving a toast for it, and never settles (the page is leaving). */
export function leaveFor(href: string, notice?: string): Promise<never> {
  if (notice) {
    try {
      window.sessionStorage.setItem(MODE_NOTICE_KEY, notice);
    } catch {
      // Storage off: the member still lands in their mode.
    }
  }
  pageLoad.load(href);
  return new Promise<never>(() => {});
}

/**
 * The sidebar item a path lights: the longest item address it sits under. Home (`/admin`) lights
 * only for itself, since every page sits under it.
 */
export function litItem(path: string, items: readonly string[]): string | null {
  let best: string | null = null;
  for (const to of items) {
    const hit =
      to === "/admin" ? path === "/admin" || path === "/admin/" : path === to || path.startsWith(`${to}/`);
    if (hit && (!best || to.length > best.length)) best = to;
  }
  return best;
}
